import { lazy, Suspense } from 'react';
import { Box, Flex, Tooltip } from '@chakra-ui/react';
import { SideNavControl, TopNavSearchBar, UserProfile } from './components';
import holidayBanner from '../../assets/svg/nav/hb.png';
import diwaliBanner from '@/assets/images/nav/diwali-banner.svg';
import { Announcement02 } from '@kula-ai/icons-core';
import { QuickCreateOptions } from './components/QuickCreateOptions';
import { useIsMobile } from '@/hooks/use-IsMobile';
import {
  getBrowserTimeZone,
  getSeasonalTheme,
  type SeasonalTheme,
} from './seasonalTheme';

const Snowfall = lazy(() => import('react-snowfall'));
const DiwaliSnowfall = lazy(() => import('./DiwaliSnowfall'));
const DiwaliFireworks = lazy(() => import('./DiwaliFireworks'));

const NAV_GRADIENT = 'linear-gradient(180deg, #1B194D 0%, #121133 100%)';

const SEASONAL_BACKGROUNDS: Record<
  SeasonalTheme,
  { backgroundImage: string; backgroundPosition: string }
> = {
  // Unchanged from before the Diwali theme.
  christmas: {
    backgroundImage: `url(${holidayBanner})`,
    backgroundPosition: 'bottom 0px right -30px',
  },
  // Anchored flush right so `cover` fills the whole bar: the rope light runs
  // in from the left edge and there's no repeat seam.
  diwali: {
    backgroundImage: `url(${diwaliBanner})`,
    backgroundPosition: 'right bottom',
  },
};

const getNavBackground = (theme: SeasonalTheme | null) =>
  theme
    ? { ...SEASONAL_BACKGROUNDS[theme], backgroundSize: 'cover' }
    : { backgroundImage: NAV_GRADIENT };

const BeamerIcon = () => {
  return (
    <Box id="showBeamer">
      <Tooltip label="What's New">
        <Flex
          width="36px"
          height="36px"
          alignItems="center"
          justifyContent="center"
          className="default"
          borderRadius="10px"
          cursor="pointer"
        >
          <Announcement02
            size={18}
            // The top nav is dark in both themes; `white` turns dark in dark mode.
            color="var(--kula-colors-static-white)"
            className="kula-icon"
          />
        </Flex>
      </Tooltip>
    </Box>
  );
};
export const TopNavBar = () => {
  const isMobile = useIsMobile();
  if (isMobile) return null;
  const theme = getSeasonalTheme(new Date(), getBrowserTimeZone());

  return (
    <Box position={'relative'}>
      <Flex
        p="6px 17px"
        w="100%"
        alignItems="center"
        {...getNavBackground(theme)}
      >
        <SideNavControl />
        <TopNavSearchBar />
        <Flex gap="6px" alignItems="center">
          {import.meta.env.VITE_BEAMER_ID && <BeamerIcon />}
          <QuickCreateOptions />
          <UserProfile />
        </Flex>
        {theme && (
          <Suspense fallback={null}>
            {theme === 'christmas' ? (
              <Snowfall snowflakeCount={20} speed={[0, 0.1]} wind={[0, 0.3]} />
            ) : (
              <>
                <DiwaliFireworks />
                <DiwaliSnowfall />
              </>
            )}
          </Suspense>
        )}
      </Flex>
    </Box>
  );
};
